/** Convert snake_case / camelCase keys to Title Case labels. */
export function humanizeLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Escape HTML for safe innerHTML injection (legacy interop) */
export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/** Clamp a number between min and max */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Format a date string to locale date */
export function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
}

/** Convert MTG mana cost string like {2}{U}{B} to array of symbols */
export function parseMana(manaCost: string): string[] {
  return [...manaCost.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);
}

/** Convert a mana symbol like "U" to mana font class */
export function manaClass(symbol: string): string {
  return `ms ms-${symbol.toLowerCase().replace('/', '')} ms-cost`;
}

/** Format large numbers with commas */
export function formatNumber(n: number): string {
  return n.toLocaleString();
}

/**
 * Build a Scryfall image URL for a card.
 * Prefers scryfall_id (specific printing) when available, falls back to name search.
 * Pass face='back' to get the back face of a double-faced card.
 */
export function scryfallImageUri(
  cardName: string,
  scryfallId?: string,
  version: 'small' | 'normal' | 'large' | 'png' | 'art_crop' | 'border_crop' = 'normal',
  face?: 'back',
): string {
  const faceParam = face ? `&face=${face}` : '';
  if (scryfallId) {
    return `https://api.scryfall.com/cards/${scryfallId}?format=image&version=${version}${faceParam}`;
  }
  return `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(cardName)}&format=image&version=${version}${faceParam}`;
}

// ── Tooltip image-size preference (persisted in localStorage) ─────────────

export type TooltipImageSize = 'normal' | 'large' | 'png';
const TOOLTIP_SIZE_KEY = 'mc_tooltip_image_size';

/** Read the user's preferred tooltip image size (defaults to 'png'). */
export function getTooltipImageVersion(): TooltipImageSize {
  try {
    const v = localStorage.getItem(TOOLTIP_SIZE_KEY);
    if (v === 'normal' || v === 'large' || v === 'png') return v;
  } catch { /* localStorage unavailable */ }
  return 'png';
}

/** Persist the user's preferred tooltip image size. */
export function setTooltipImageVersion(size: TooltipImageSize): void {
  try {
    localStorage.setItem(TOOLTIP_SIZE_KEY, size);
  } catch { /* localStorage unavailable */ }
}

// ── Card name display helpers ─────────────────────────────────────────────

/**
 * Return the display name for a card.
 *
 * Adventure and omen cards store the full Scryfall name "Creature // Spell"
 * but should be displayed using only the front-face (creature) name.
 * Split cards intentionally keep both halves visible.
 */
export function displayCardName(name: string, layout?: string | null): string {
  if (layout === 'adventure' && name.includes(' // ')) {
    return name.split(' // ')[0];
  }
  return name;
}

/** Per-face display descriptor for the card tooltip. */
export type CardFaceDisplay = { src: string; rotate?: 90 | 180 };

/**
 * Returns layout-aware face descriptors for the card tooltip.
 *
 * Layout handling:
 *  - split / planar: landscape cards — rotated 90° so the text reads horizontally
 *  - adventure: portrait card with creature + spell on the same face — no rotation
 *  - flip: same image shown twice, second at 180°
 *  - transform / modal_dfc / double_faced_token / reversible_card / battle: two portrait faces
 *  - meld: only the front face is shown (the meld result requires a separate lookup)
 *  - unknown (null/undefined) + name contains " // ": try back-face; CardTooltip hides it on 404
 *  - normal / everything else: single upright image
 *
 * Image version is read from the user's localStorage preference (normal/large/png).
 */
export function cardImageUris(
  cardName: string,
  scryfallId?: string,
  layout?: string | null,
): CardFaceDisplay[] {
  const version = getTooltipImageVersion();
  // For adventure cards, look up only the front-face name so the API resolves correctly.
  const frontName = (layout === 'adventure' && cardName.includes(' // '))
    ? cardName.split(' // ')[0]
    : cardName;
  const front = scryfallImageUri(frontName, scryfallId, version);
  const back  = scryfallImageUri(cardName,  scryfallId, version, 'back');

  switch (layout) {
    case 'split':
    case 'planar':
      // Truly landscape cards — rotate 90° so the text reads correctly
      return [{ src: front, rotate: 90 }];

    case 'adventure':
      // Portrait card: both the creature and the adventure spell appear on the
      // same face without any rotation.  Only the front face is relevant.
      return [{ src: front }];

    case 'flip':
      // Same image, second orientation is 180° rotated
      return [{ src: front }, { src: front, rotate: 180 }];

    case 'battle':
    case 'transform':
    case 'modal_dfc':
    case 'double_faced_token':
    case 'reversible_card':
      return [{ src: front }, { src: back }];

    default:
      // If we have no layout data but the name has " // ", speculatively try the back face.
      // CardTooltip hides images that 404 (e.g. for split cards fetched by name only).
      if (cardName.includes(' // ')) {
        return [{ src: front }, { src: back }];
      }
      return [{ src: front }];
  }
}

/** Normalize color_identity from backend (may be string or string[]) */
export function normalizeColorIdentity(ci: string | string[] | undefined): string[] {
  if (!ci) return [];
  if (Array.isArray(ci)) return ci.map(c => c.toUpperCase());
  return ci.split(',').map(c => c.trim().toUpperCase()).filter(Boolean);
}

/** Coerce unknown value to string, returning undefined for nullish values */
export function str(v: unknown): string | undefined {
  return v != null ? String(v) : undefined;
}

/** Derive a human-friendly platform name from a deck URL */
export function sourcePlatform(url: string): string {
  if (url.includes('moxfield')) return 'Moxfield';
  if (url.includes('archidekt')) return 'Archidekt';
  return 'Source';
}
